import React from 'react';
import { motion } from 'framer-motion';
import { gstUtils } from '../lib/calculateGST';

interface DashboardCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  trend?: {
    value: number;
    isPositive: boolean;
    period: string;
  };
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'gray';
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = 'primary',
  isLoading = false,
  onClick,
  className = '',
}) => {
  const colorClasses = {
    primary: {
      bg: 'bg-primary-50',
      icon: 'text-primary-600',
      text: 'text-primary-900',
      border: 'border-primary-200',
    },
    success: {
      bg: 'bg-success-50',
      icon: 'text-success-600',
      text: 'text-success-900',
      border: 'border-success-200',
    },
    warning: {
      bg: 'bg-warning-50',
      icon: 'text-warning-600',
      text: 'text-warning-900',
      border: 'border-warning-200',
    },
    danger: {
      bg: 'bg-danger-50',
      icon: 'text-danger-600',
      text: 'text-danger-900',
      border: 'border-danger-200',
    },
    gray: {
      bg: 'bg-gray-50',
      icon: 'text-gray-600',
      text: 'text-gray-900',
      border: 'border-gray-200',
    },
  };

  const colors = colorClasses[color];

  const formatValue = (val: string | number): string => {
    if (typeof val === 'number') {
      // Check if it looks like a currency amount
      if (val > 0 && val < 1000000) {
        return gstUtils.formatCurrency(val);
      }
      return val.toLocaleString();
    }
    return val;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={onClick ? { scale: 1.02 } : {}}
      className={`
        card relative overflow-hidden transition-all duration-200
        ${onClick ? 'cursor-pointer hover:shadow-md' : ''}
        ${colors.border} border-l-4
        ${className}
      `}
      onClick={onClick}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="spinner w-6 h-6"></div>
        </div>
      )}

      <div className="flex items-center">
        {Icon && (
          <div className={`flex-shrink-0 p-3 rounded-lg ${colors.bg}`}>
            <Icon className={`h-6 w-6 ${colors.icon}`} />
          </div>
        )}

        <div className={`${Icon ? 'ml-4' : ''} flex-1`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className={`text-2xl font-bold ${colors.text}`}>{formatValue(value)}</p>
              {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>

            {trend && (
              <div className="text-right">
                <div
                  className={`flex items-center text-sm font-medium ${
                    trend.isPositive ? 'text-success-600' : 'text-danger-600'
                  }`}
                >
                  <span
                    className={`inline-block w-0 h-0 mr-1 ${
                      trend.isPositive
                        ? 'border-l-2 border-r-2 border-b-2 border-transparent border-b-success-600'
                        : 'border-l-2 border-r-2 border-t-2 border-transparent border-t-danger-600'
                    }`}
                  />
                  {Math.abs(trend.value)}%
                </div>
                <p className="text-xs text-gray-500">{trend.period}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardCard;
